import React, { useId } from 'react';
import { Minus, Plus } from 'lucide-react';

/**
 * Accessible unit counter with decrement/increment and companion numeric input.
 */
export default function PublicProspectUnitCounter({
  label,
  value,
  onChange,
  disabled,
  decrementLabel,
  incrementLabel,
  error,
  errorId,
}) {
  const id = useId();
  const inputId = `${id}-count`;
  const setValue = (next) => {
    const clamped = Math.max(0, Math.min(9999, Number.isFinite(next) ? next : 0));
    onChange(clamped);
  };

  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <label className="block font-medium text-[#004747]" htmlFor={inputId}>
        {label}
      </label>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-gray-300 bg-white text-[#004747] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] disabled:opacity-50"
          aria-label={`${decrementLabel}: ${label}`}
          disabled={disabled || value <= 0}
          onClick={() => setValue(value - 1)}
        >
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          id={inputId}
          className="w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2.5 text-center text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91]"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={String(value)}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const raw = event.target.value.trim();
            if (raw === '') {
              onChange(0);
              return;
            }
            if (!/^\d{1,4}$/.test(raw)) return;
            setValue(Number(raw));
          }}
        />
        <button
          type="button"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-gray-300 bg-white text-[#004747] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] disabled:opacity-50"
          aria-label={`${incrementLabel}: ${label}`}
          disabled={disabled || value >= 9999}
          onClick={() => setValue(value + 1)}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {error ? (
        <p id={errorId} className="mt-1 text-sm font-medium text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
